
export const UTILIZATION = {
  hoursPerWeek: 40,
  utilizationFactor: 0.8, 
} as const;

export const THRESHOLDS = {
  gapCriticalFTE: 0.5, 
  gapWarningFTE: 0.2,
  overstaffAmberFTE: -0.3, 
  dscr: { green: 1.35, amber: 1.2 }, 
  icr: { green: 2.0, amber: 1.5 }, 
  hkRoomsPerAttendant: { low: 10, high: 25 }, 
} as const;

export const CAPACITIES = {
  fbService: { coversPerServerPerHour: 12 },
  bar: { coversPerBartenderPerHour: 25 },
  kitchen: { coversPerChefPerHour: 20 },
  housekeeping: { roomsPerAttendantPerShift: 15, shiftHours: 8 },
  spa: { treatmentsPerTherapistPerHour: 1, defaultOpenHours: 10 },
} as const;

export const BENCHMARKS = {
  housekeeping: "12–18 rooms/attendant per 8h shift (10 low, 25+ unrealistic)",
  frontOffice:
    "≈5.25 FTE per 24/7 post (168 hrs/week ÷ ~32 productive hrs/FTE)",
  fbService: "10–14 diners per server per hour",
  kitchen: "15–25 diners per chef per hour",
  bar: "20–30 drinks/covers per bartender per hour",
  spa: "5–7 treatments/therapist/day (bookable ~6 hrs/day)",
} as const;

export function getBenchmarkText(dept: string, role: string): string {
  switch (dept) {
    case "frontOffice":
      return BENCHMARKS.frontOffice;
    case "housekeeping":
      return BENCHMARKS.housekeeping;
    case "fbService":
      return BENCHMARKS.fbService;
    case "kitchen":
      return BENCHMARKS.kitchen;
    case "bar":
      return BENCHMARKS.bar;
    case "wellness":
      return BENCHMARKS.spa;
    default:
      return "Industry standard productivity benchmarks";
  }
}

export function getCalculationSummary(
  dept: string,
  assumptions: any,
  roomsSoldPerDay?: number,
  overrides: any = {} 
): string {
  const frontOfficeAssum = assumptions.frontOffice || {};
  const housekeepingAssum = assumptions.housekeeping || {};
  const fbServiceAssum = assumptions.fbService || {};
  const kitchenAssum = assumptions.kitchen || {};
  const barAssum = assumptions.bar || {};
  const wellnessAssum = assumptions.wellness || {};

  const housekeepingOverrides = overrides.housekeeping || {};
  const wellnessOverrides = overrides.spa || {}; // В твоем коде был 'spa', возможно опечатка

  switch (dept) {
    case "frontOffice":
      // Используем данные из API: minutesPerArrival и minutesPerDeparture
      return `Based on ${frontOfficeAssum.minutesPerArrival} min/arrival & ${frontOfficeAssum.minutesPerDeparture} min/departure`;

    case "housekeeping":
      // Используем данные из API: minutesPerRoomClean
      const minsPerRoom =
        housekeepingOverrides.minutesPerRoomClean ??
        housekeepingAssum.minutesPerRoomClean;
      return `${roomsSoldPerDay?.toFixed(0) || 0} rooms/day @ ${minsPerRoom} min/room`;

    case "fbService":
      // В API нет 'Active' флагов, используем 'openHours'
      return `Service over ${fbServiceAssum.openHours} hours`;

    case "kitchen":
      // Используем данные из API: coversPerHour
      return `Covers @ ${kitchenAssum.coversPerHour}/hr over ${kitchenAssum.openHours} hrs`;

    case "bar":
      // Используем данные из API: drinksPerHour
      return `Drinks @ ${barAssum.drinksPerHour}/hr over ${barAssum.openHours} hrs`;

    case "wellness":
      // Используем данные из API: treatmentsPerTherapist
      const treatments =
        wellnessOverrides.treatmentsPerDay ??
        wellnessAssum.treatmentsPerTherapist; // У тебя в API 'treatmentsPerTherapist'
      return `${treatments} treatment${treatments === 1 ? "" : "s"}/day over ${wellnessAssum.openHours} hours`;

    // Добавим 'rooms', если он используется
    case "rooms":
      const foMins = frontOfficeAssum.minutesPerArrival || "?";
      const hkMins = housekeepingAssum.minutesPerRoomClean || "?";
      return `FO (${foMins} min) & HK (${hkMins} min)`;

    default:
      return "Based on operational requirements";
  }
}

// Optional aggregator (safe):
const staffingBenchmarks = { UTILIZATION, THRESHOLDS, CAPACITIES, BENCHMARKS };
export default staffingBenchmarks;
